import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ThirdwebWalletConnect } from "./ThirdwebWalletConnect";
import { BkfcHeaderWallet } from "../bkfc/components/BkfcHeaderWallet";
import { useHttp } from "../hooks/useHttp";

const truncateEmail = (email: string, startChars = 3, endChars = 2): string => {
  const [localPart, domain] = email.split('@');
  if (!domain) return email; // Invalid email format

  if (localPart.length <= startChars + endChars) {
    return email; // Too short to truncate meaningfully
  }

  return `${localPart.slice(0, startChars)}...${localPart.slice(-endChars)}@${domain}`;
};

export function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const { sendRequest } = useHttp();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [showEmailDropdown, setShowEmailDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const emailDropdownRef = useRef<HTMLDivElement>(null);

  const isClaimPage = location.pathname === "/claim/catgame";
  const isWallpaperPage = location.pathname === "/catgame-wallpaper";
  const isRewardsPage = location.pathname === "/rewards/catgame";
  const isRewardPage = location.pathname === "/catgame-reward";
  const isTeddymooPage = location.pathname === '/teddymoo';
  const isCatGamePage = isClaimPage || isWallpaperPage || isRewardsPage || isRewardPage;

  const isExpoDemoClaimPage = location.pathname === "/claim/expodemo";
  const isExpoDemoRewardsPage = location.pathname === "/rewards/expodemo";
  const isExpoDemoPage = isExpoDemoClaimPage || isExpoDemoRewardsPage;

  const isBkfcPage =
    location.pathname === "/claim/bkfc" ||
    location.pathname.startsWith("/rewards/bkfc");

  const navigation = [
    // BKFC-only: burger-menu path to the certificates/rewards page
    ...(isBkfcPage
      ? [
          {
            name: "My certificates",
            href: "/rewards/bkfc",
            icon: "/svg/document-icon.svg",
            active: location.pathname.startsWith("/rewards/bkfc"),
            boldIcon: "/svg/document-icon.svg",
          },
        ]
      : []),
    {
      name: "Support",
      href: "/support",
      icon: "/svg/customer-support-icon.svg",
      active: location.pathname === "/support",
      boldIcon: "/svg/customer-support-icon.svg",
    },
  ];

  const isEmailAuthPage = isCatGamePage || isExpoDemoPage;
  const isOnClaimEntryPage = isClaimPage || isExpoDemoClaimPage;
  const authBase = isExpoDemoPage ? "expodemo" : "catgame";
  const profileRedirect = isExpoDemoPage ? "/rewards/expodemo" : "/rewards/catgame";

  useEffect(() => {
    if (!isEmailAuthPage) {
      setIsCheckingAuth(false);
      return;
    }

    const checkAuth = async () => {
      try {
        const { data } = await sendRequest<{
          authenticated: boolean;
          email?: string;
        }>({
          endpoint: `/${authBase}/session`,
          method: 'GET',
          responseType: 'json',
        });

        setIsAuthenticated(data.authenticated);
        setUserEmail(data.email || null);
      } catch (err) {
        setIsAuthenticated(false);
        setUserEmail(null);
      } finally {
        setIsCheckingAuth(false);
      }
    };

    checkAuth();

    // Listen for auth status changes from other components
    const handleAuthChange = () => {
      checkAuth();
    };

    window.addEventListener('authStatusChanged', handleAuthChange);
    return () => {
      window.removeEventListener('authStatusChanged', handleAuthChange);
    };
  }, [location.pathname, isEmailAuthPage, authBase, sendRequest]);

  const handleLogout = async () => {
    try {
      await sendRequest({
        endpoint: `/${authBase}/logout`,
        method: 'POST',
        responseType: 'json',
      });

      setIsAuthenticated(false);
      setUserEmail(null);
      setShowEmailDropdown(false);
      window.location.reload();
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
      if (
        emailDropdownRef.current &&
        !emailDropdownRef.current.contains(event.target as Node)
      ) {
        setShowEmailDropdown(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      <header className="sticky top-0 z-50 bg-white">
        <nav className="px-6 mx-auto max-w-md">
          <div className="flex justify-between items-center h-16">
            <div>
              <img src="/svg/223-logo.svg" alt="223 Logo" />
            </div>

            {
              !isTeddymooPage && (
                <div className="flex items-center space-x-3">
                  {isEmailAuthPage ? (
                    !isCheckingAuth && (
                      <>
                        {isOnClaimEntryPage ? (
                          isAuthenticated && userEmail && (
                            <div className="relative" ref={emailDropdownRef}>
                              <button
                                onClick={() => setShowEmailDropdown(!showEmailDropdown)}
                                style={{
                                  backgroundColor: "transparent",
                                  color: "black",
                                  border: "2px solid black",
                                  padding: "2px 10px",
                                  fontWeight: "bold",
                                  fontFamily: "inherit",
                                  lineHeight: "1",
                                  borderRadius: "0px",
                                  minWidth: "fit-content",
                                  minHeight: "35px",
                                }}
                              >
                                {truncateEmail(userEmail)}
                              </button>

                              {showEmailDropdown && (
                                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-md border shadow-lg z-50">
                                  <div className="w-[25px] h-[25px] bg-white rotate-45 rounded-lg border absolute top-[-12px] right-[12px] border-r-0 border-b-0"></div>
                                  <button
                                    onClick={handleLogout}
                                    className="w-full text-left px-4 py-3 hover:bg-gray-100 text-[#1F1F1F] font-medium"
                                  >
                                    Logout
                                  </button>
                                </div>
                              )}
                            </div>
                          )
                        ) : (
                          <div className="relative" ref={emailDropdownRef}>
                            <button
                              onClick={() => {
                                if (isAuthenticated) {
                                  setShowEmailDropdown(!showEmailDropdown);
                                } else {
                                  navigate(profileRedirect);
                                }
                              }}
                              style={{
                                backgroundColor: "transparent",
                                color: "black",
                                border: "2px solid black",
                                padding: "2px 10px",
                                fontWeight: "bold",
                                fontFamily: "inherit",
                                lineHeight: "1",
                                borderRadius: "0px",
                                minWidth: "fit-content",
                                minHeight: "35px",
                              }}
                            >
                              {isAuthenticated && userEmail ? truncateEmail(userEmail) : 'Login'}
                            </button>

                            {showEmailDropdown && isAuthenticated && (
                              <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-md border shadow-lg z-50">
                                <div className="w-[25px] h-[25px] bg-white rotate-45 rounded-lg border absolute top-[-12px] right-[12px] border-r-0 border-b-0"></div>
                                <button
                                  onClick={handleLogout}
                                  className="w-full text-left px-4 py-3 hover:bg-gray-100 text-[#1F1F1F] font-medium"
                                >
                                  Logout
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )
                  ) : isBkfcPage ? (
                    // wordmark + connected-address chip with a disconnect
                    // dropdown; safe here because every BKFC route mounts
                    // Header inside BkfcAptosProvider
                    <BkfcHeaderWallet />
                  ) : (
                    <ThirdwebWalletConnect
                      showDropdown={true}
                      onWalletDetailsClick={() => setShowDropdown(false)}
                      btnTitle="Sign up"
                    />
                  )}

                  <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
                    <img src="/jam_menu.png" alt="Menu" className="" />
                  </button>
                </div>
              )
            }


            {
              isTeddymooPage && (
                <img src="/svg/teddymoo/teddymoo-logo.svg" alt="TeddyMoo Logo" />
              )
            }

          </div>

          {isMobileMenuOpen && (
            <div className="absolute right-6 top-[70] w-full max-w-[235px]">
              <div className="relative bg-white rounded-md border shadow-lg">
                <div className="w-[25px] h-[25px] bg-white rotate-45 rounded-lg border absolute top-[-12px] right-[3px] border-r-0 border-b-0"></div>

                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="flex items-center my-5 space-x-2.5 text-[#1F1F1F] px-3.5"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <img
                      src={item.active ? item.boldIcon : item.icon}
                      alt={item.name}
                    />

                    <span className={`${item.active ? "font-bold" : ""}`}>
                      {item.name}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </nav>
      </header>
    </>
  );
}